import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import * as React from "react";

import {
  CHART_DISPLAY_TYPES,
  ChartDisplayType,
  NumericAggregation,
  NUMERIC_AGGREGATIONS,
} from "@/shared/domain/metrics";

import {
  CardConfig,
  ChartCardConfig,
  RawChartCardConfig,
} from "../DashboardHeader/DashboardParams";

import styles from "./CardEditFields.module.css";
import { SearchSelect } from "./SearchSelect";

/** The two things a chart card can plot. Not a persisted enum: the card
 *  itself says which it is by carrying an aggregation or not — this is only
 *  the picker's own vocabulary.
 *
 *  The labels name what happens to the SESSIONS in a period, which is the
 *  choice being made: collapse them into one value, or plot every one. */
const SERIES_OPTIONS = [
  { value: "aggregated", label: "Aggregated by period" },
  { value: "raw", label: "None (show all sessions)" },
] as const;

type SeriesKind = (typeof SERIES_OPTIONS)[number]["value"];

/** What a chart card falls back to when the user asks for an aggregated
 *  series and the panel has nothing remembered. Matches the defaults a freshly
 *  added chart card is created with. */
const DEFAULT_AGGREGATION: NumericAggregation = "mean";
const DEFAULT_DISPLAY: ChartDisplayType = "line";

export interface CardEditFieldsProps {
  /** The card being edited (controlled by the parent). */
  card: CardConfig;
  /** All selectable metric names. */
  allNames: string[];
  /** The metric to show selected — the card's own, or its resolved fallback
   *  when unset/stale (see effectiveTrendMetric / effectiveChartMetric). */
  effectiveMetric: string;
  /** Fired with the updated card whenever the user changes a picker. */
  onChange: (next: CardConfig) => void;
}

/**
 * A card's data pickers — metric, and then whatever else the card's kind and
 * series have to say for themselves.
 *
 * A chart card additionally chooses its SERIES: an aggregate per period
 * bucket, or every value the metric has. Those are mutually exclusive, and
 * the card type says so by having an aggregation or not — so the aggregation
 * and display pickers exist only on the aggregated branch rather than sitting
 * there inert on the other.
 *
 * Surface-agnostic: {@link CardEditPopover} hangs them off a card's ⋮ menu,
 * and the add-card flow slides them in behind its type picker.
 */
export function CardEditFields({
  card,
  allNames,
  effectiveMetric,
  onChange,
}: CardEditFieldsProps) {
  // What to restore if the user switches a chart back to Aggregated. This is
  // transient UI memory, not card data: a raw card genuinely has no
  // aggregation, so storing one on it to make the toggle feel nice would put
  // a value in the persisted definition that nothing reads.
  const [rememberedAggregation, setRememberedAggregation] =
    React.useState<NumericAggregation>(card.aggregation ?? DEFAULT_AGGREGATION);
  const [rememberedDisplay, setRememberedDisplay] =
    React.useState<ChartDisplayType>(
      (card.kind === "chart" ? card.display : undefined) ?? DEFAULT_DISPLAY,
    );

  const isChart = card.kind === "chart";
  // Narrowed once, so the aggregation-dependent fields below are built from a
  // card the type system agrees HAS an aggregation — a trend tile or an
  // aggregated chart. `null` is exactly the raw chart card.
  const aggregatedCard = card.aggregation === undefined ? null : card;
  const isRawChart = aggregatedCard === null;

  /** Swap a chart card between its two variants. Builds the target variant
   *  explicitly rather than spreading and patching, so the card handed to
   *  `onChange` can never be a half-converted shape carrying both an
   *  aggregation and the absence that means "raw". */
  const changeSeries = (next: SeriesKind, chartCard: ChartCardConfig) => {
    const base: RawChartCardConfig = {
      id: chartCard.id,
      kind: "chart",
      metric: chartCard.metric,
      ...(chartCard.placement ? { placement: chartCard.placement } : {}),
      ...(chartCard.section ? { section: chartCard.section } : {}),
    };
    onChange(
      next === "raw"
        ? base
        : {
            ...base,
            aggregation: rememberedAggregation,
            display: rememberedDisplay,
          },
    );
  };

  return (
    <div className={styles.editor}>
      <SearchSelect
        label="Metric"
        value={effectiveMetric}
        options={allNames}
        onChange={(next) => onChange({ ...card, metric: next })}
      />
      {isChart && (
        <FormControl size="small">
          <InputLabel id={`card-edit-series-${card.id}`}>
            Session Aggregation
          </InputLabel>
          <Select
            labelId={`card-edit-series-${card.id}`}
            label="Session Aggregation"
            value={isRawChart ? "raw" : "aggregated"}
            onChange={(e: SelectChangeEvent) =>
              changeSeries(e.target.value as SeriesKind, card)
            }
          >
            {SERIES_OPTIONS.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {aggregatedCard !== null && (
        <SearchSelect
          label="Aggregation"
          value={aggregatedCard.aggregation}
          options={[...NUMERIC_AGGREGATIONS]}
          onChange={(next) => {
            const aggregation = next as NumericAggregation;
            setRememberedAggregation(aggregation);
            onChange({ ...aggregatedCard, aggregation });
          }}
        />
      )}
      {aggregatedCard !== null && aggregatedCard.kind === "chart" && (
        <FormControl size="small">
          <InputLabel id={`card-edit-display-${card.id}`}>Display</InputLabel>
          <Select
            labelId={`card-edit-display-${card.id}`}
            label="Display"
            value={aggregatedCard.display}
            onChange={(e: SelectChangeEvent) => {
              const display = e.target.value as ChartDisplayType;
              setRememberedDisplay(display);
              onChange({ ...aggregatedCard, display });
            }}
          >
            {CHART_DISPLAY_TYPES.map((d) => (
              <MenuItem key={d.value} value={d.value}>
                {d.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </div>
  );
}
